import React, { useEffect, useState, useCallback, useMemo } from "react";
import { TrendingUp, TrendingDown, RefreshCw } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import CandlestickChart from "../components/CandlestickChart";
import SignalCard from "../components/SignalCard";
import { logError } from "../lib/logger";

const CATEGORIES = {
  crypto: "Criptovalute",
  commodities: "Materie Prime",
  forex: "Valute (Forex)",
};

const INTERVALS = [
  { id: "15min", label: "15M" },
  { id: "1h", label: "1H" },
  { id: "4h", label: "4H" },
  { id: "1day", label: "1G" },
  { id: "1week", label: "1S" },
];

function fmtPrice(p, cat) {
  if (p == null) return "—";
  if (cat === "forex") return p.toFixed(4);
  if (p >= 1000) return p.toLocaleString("it-IT", { maximumFractionDigits: 2 });
  return p.toFixed(2);
}

// Marker/legend colour: filled LIMIT orders use blue/white, everything else green/red.
function markerColor(direction, isFilledLimit) {
  const buy = direction === "BUY";
  if (isFilledLimit) return buy ? "#3B82F6" : "#FFFFFF";
  return buy ? "#10B981" : "#EF4444";
}

export default function Markets() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [interval, setIntervalVal] = useState("1day");
  const [candles, setCandles] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [signals, setSignals] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const paramSymbol = searchParams.get("symbol");

  // Select the asset from ?symbol= (e.g. a notification click) as soon as quotes are available,
  // overriding any current selection; otherwise ensure a sensible default is selected.
  useEffect(() => {
    if (!quotes.length) return;
    if (paramSymbol) {
      const q = quotes.find((x) => x.display === paramSymbol || x.symbol === paramSymbol);
      if (q && !q.unavailable) {
        setSelected(q);
        setSearchParams({}, { replace: true });
        return;
      }
    }
    setSelected((prev) => prev || quotes.find((q) => !q.unavailable) || quotes[0]);
  }, [quotes, paramSymbol, setSearchParams]);

  const loadSignals = useCallback(async () => {
    try {
      const { data } = await api.get("/signals");
      setSignals(data);
    } catch (e) {
      logError("Errore caricamento segnali per il grafico:", e);
    }
  }, []);

  useEffect(() => { loadSignals(); }, [loadSignals]);

  const loadQuotes = useCallback(async () => {
    try {
      const { data } = await api.get("/market/quotes");
      setQuotes(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Errore dati di mercato");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadQuotes(); }, [loadQuotes]);
  useEffect(() => {
    const t = setInterval(loadQuotes, 60000);
    return () => clearInterval(t);
  }, [loadQuotes]);

  const loadChart = useCallback(async (symbol, itv) => {
    setChartLoading(true);
    try {
      const { data } = await api.get(`/market/timeseries?symbol=${encodeURIComponent(symbol)}&interval=${itv}`);
      setCandles(data.candles || []);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Grafico non disponibile (limite richieste). Riprova tra poco.");
      setCandles([]);
    } finally { setChartLoading(false); }
  }, []);

  useEffect(() => {
    if (selected && !selected.unavailable) loadChart(selected.symbol, interval);
  }, [selected, interval, loadChart]);

  const grouped = Object.keys(CATEGORIES).map((cat) => ({
    cat, label: CATEGORIES[cat], items: quotes.filter((q) => q.category === cat),
  })).filter((g) => g.items.length);

  const pairSignals = useMemo(() => (
    selected
      ? signals.filter((s) => s.symbol === selected.display && s.status === "active" && s.entry != null)
      : []
  ), [selected, signals]);

  // Entry lines (pending LIMIT / manual) + TP & SL lines for every active signal of the pair.
  const priceLines = useMemo(() => {
    const lines = [];
    for (const s of pairSignals) {
      if (s.order_type === "LIMIT" && !s.filled) {
        // Pending LIMIT: dashed line, light-blue for BUY / red for SELL (no marker until filled).
        lines.push({ price: s.entry, color: s.direction === "BUY" ? "#93c5fd" : "#EF4444",
          lineStyle: 2, title: `${s.direction} LIMIT ${s.timeframe}` });
      } else {
        // NOW / filled LIMIT / manual: solid "Entry" line (the arrow marker is added separately).
        lines.push({ price: s.entry, color: "#93c5fd", lineStyle: 0, title: `Entry ${s.timeframe}` });
      }
      if (s.tp_price != null) lines.push({ price: s.tp_price, color: "#10B981", lineStyle: 2, title: `TP ${s.timeframe}` });
      if (s.sl_price != null) lines.push({ price: s.sl_price, color: "#EF4444", lineStyle: 2, title: `SL ${s.timeframe}` });
    }
    return lines;
  }, [pairSignals]);

  const startTimeOf = (s) => (
    (s.order_type === "LIMIT" && s.filled_at)
      ? Math.floor(new Date(s.filled_at).getTime() / 1000) : s.signal_bar_time
  );

  // Closed trades of this pair (limited to the most recent).
  const closed = useMemo(() => (
    selected
      ? signals.filter((s) => s.symbol === selected.display
          && (s.status === "tp_hit" || s.status === "sl_hit")
          && s.entry != null && s.closed_at).slice(0, 20)
      : []
  ), [selected, signals]);

  // Markers on the candlestick series: only entries of ACTIVE signals (NOW / filled LIMIT).
  // Closed-trade markers are attached to their own diagonal line series (see `trades`).
  const markers = useMemo(() => {
    const arr = [];
    for (const s of pairSignals) {
      if (s.order_type === "NOW" || (s.order_type === "LIMIT" && s.filled)) {
        const buy = s.direction === "BUY";
        const isNow = s.order_type === "NOW";
        const color = markerColor(s.direction, !isNow);
        const t = isNow ? s.signal_bar_time : startTimeOf(s);
        arr.push({ time: t, position: buy ? "belowBar" : "aboveBar", color,
          shape: buy ? "arrowUp" : "arrowDown", text: `${s.direction} ${isNow ? "NOW" : "LIMIT"} ${s.timeframe}` });
        arr.push({ time: t, position: "inBar", color, shape: "circle" });
      }
    }
    return arr.filter((m) => m.time);
  }, [pairSignals]);

  // Closed trades: dashed diagonal from entry to the level hit; colour by direction (BUY blu / SELL bianco).
  // Entry (arrow + filled dot) and exit (filled dot + "TP"/"SL") markers are rendered on this line series
  // so their labels sit exactly on the trade's own price levels.
  const trades = useMemo(() => closed.map((s) => {
    const win = s.status === "tp_hit";
    return {
      startTime: startTimeOf(s), startPrice: s.entry,
      endTime: Math.floor(new Date(s.closed_at).getTime() / 1000),
      endPrice: win ? s.tp_price : s.sl_price,
      color: s.direction === "BUY" ? "#3B82F6" : "#FFFFFF",
      direction: s.direction,
      timeframe: s.timeframe,
      exitLabel: win ? "TP" : "SL",
    };
  }).filter((t) => t.startTime && t.endTime && t.endPrice != null), [closed]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Mercati</h1>
          <p className="text-sm text-zinc-400 mt-1">Prezzi live e grafici a candele · Cripto · Materie Prime · Valute</p>
        </div>
        <button data-testid="refresh-markets-btn" onClick={loadQuotes}
          className="p-2 rounded-md hover:bg-white/5 transition-colors" title="Aggiorna">
          <RefreshCw size={18} className="text-zinc-400" />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Watchlist */}
        <div className="lg:col-span-1 space-y-6">
          {loading ? (
            <div className="text-center py-16 text-zinc-500">Caricamento quotazioni...</div>
          ) : grouped.map((g) => (
            <div key={g.cat}>
              <h3 className="text-xs text-zinc-500 uppercase tracking-wider mb-2">{g.label}</h3>
              <div className="space-y-1.5">
                {g.items.map((q) => {
                  const up = (q.percent_change ?? 0) >= 0;
                  const active = selected?.symbol === q.symbol;
                  return (
                    <button key={q.symbol} data-testid={`market-row-${q.symbol}`}
                      disabled={q.unavailable}
                      onClick={() => setSelected(q)}
                      className={`w-full flex items-center justify-between p-3 rounded-md border transition-colors text-left ${
                        active ? "bg-[#1a1a1a] border-blue-500/50" : "bg-[#141414] border-white/10 hover:bg-[#1a1a1a]"
                      } ${q.unavailable ? "opacity-40 cursor-not-allowed" : ""}`}>
                      <div>
                        <div className="font-mono text-sm font-semibold text-white">{q.symbol}</div>
                        <div className="text-xs text-zinc-500">{q.name}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-sm text-white">{fmtPrice(q.price, q.category)}</div>
                        {q.percent_change != null && (
                          <div className={`flex items-center justify-end gap-0.5 text-xs font-mono ${up ? "text-emerald-400" : "text-red-400"}`}>
                            {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                            {up ? "+" : ""}{q.percent_change.toFixed(2)}%
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Chart */}
        <div className="lg:col-span-2">
          <div className="bg-[#141414] border border-white/10 rounded-md p-5">
            {selected ? (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div>
                    <div className="font-mono text-xl font-semibold text-white">{selected.symbol}</div>
                    <div className="text-sm text-zinc-400">{selected.name}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="font-mono text-2xl font-semibold text-white">{fmtPrice(selected.price, selected.category)}</div>
                      {selected.percent_change != null && (
                        <div className={`font-mono text-sm ${(selected.percent_change ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                          {selected.change != null && `${selected.change >= 0 ? "+" : ""}${fmtPrice(selected.change, selected.category)} `}({selected.percent_change?.toFixed(2)}%)
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-1.5 mb-4">
                  {INTERVALS.map((it) => (
                    <button key={it.id} data-testid={`interval-${it.id}`} onClick={() => setIntervalVal(it.id)}
                      className={`px-3 py-1 rounded-sm text-xs font-mono transition-colors ${
                        interval === it.id ? "bg-white text-black" : "bg-white/5 text-zinc-400 hover:text-white"}`}>
                      {it.label}
                    </button>
                  ))}
                </div>

                {chartLoading ? (
                  <div className="h-[420px] flex items-center justify-center text-zinc-500">Caricamento grafico...</div>
                ) : candles.length ? (
                  <CandlestickChart data={candles} priceLines={priceLines} markers={markers} trades={trades} />
                ) : (
                  <div className="h-[420px] flex items-center justify-center text-zinc-500 text-center px-4">
                    Grafico non disponibile al momento (limite richieste API). Riprova tra qualche secondo.
                  </div>
                )}
              </>
            ) : (
              <div className="h-[420px] flex items-center justify-center text-zinc-500">Seleziona uno strumento per vedere il grafico.</div>
            )}
          </div>
          <p className="text-xs text-zinc-600 mt-3">Dati forniti da Capital.com (aggiornamento ogni ~60s).</p>

          {selected && pairSignals.length > 0 && (
            <div className="mt-6" data-testid="pair-active-trades">
              <h3 className="text-sm font-semibold text-white mb-3">
                Trade attivi su <span className="font-mono">{selected.symbol}</span>{" "}
                <span className="text-zinc-500 font-normal">({pairSignals.length})</span>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {pairSignals.map((s) => (
                  <SignalCard key={s.id} s={s} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
